import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialog } from "@angular/material";

import { ModuleConfiguration } from '../security.module.config';
import * as fromStore from '../store/reducers';
import * as securityActions from '../store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { Subject } from 'rxjs/Subject';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { RoleModel } from '../models/RoleModel';
import { LookupModel } from '../../common/models/LookupModel';
import { ManagePermissionsDialogComponent } from './manage-permissions-dialog.component';
import { ManagePermissionsContext } from '../models/ManagePermissionsContext';
import { ManageRolesDialogComponent } from './manage-roles-dialog.component';
import { ManageRolesContext } from '../models/ManageRolesContext';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { FormArray } from '@angular/forms/src/model';
import { LookupService } from '../../core/services/index';
import { SystemRoleType } from '../models/SystemRoleType';
import { DeleteRoleConfirmationDialogComponent } from './delete-role-confirmation-dialog.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-role-workspace',
  templateUrl: './role-workspace.component.html',
  styleUrls: ['./role-workspace.component.scss']
})
export class RoleWorkspaceComponent implements OnInit, OnDestroy {
  role: RoleModel;
  key: string;
  loading$: Observable<boolean>;
  errors$: Observable<string[]>;
  assignedRoles$: Observable<RoleModel[]>;
  assignedPermissions$: Observable<LookupModel[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  formArrayInitMethods = new Map<string, () => void>();
  profileForm: FormGroup;
  roleTypes: LookupModel[];

  editPermission: string = 'SecurityModule.Roles:Edit';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private lookupService: LookupService) {
    this.roleTypes = lookupService.lookupsForEnums(SystemRoleType);
  }

  ngOnDestroy() {
    this.store.dispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, RoleModel));
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromStore.getRoleListLoading);
    this.errors$ = this.store.select(fromStore.getRoleListErrors);
    this.assignedRoles$ = this.store.select(fromStore.getRoleAssignedRoles);
    this.assignedPermissions$ = this.store.select(fromStore.getRoleAssignedPermissions);

    this.route.params
      .takeUntil(this.destroyed$)
      .subscribe(p => {
      this.key = p['key'];

      this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, RoleModel, { key: this.key }));
    });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, RoleModel, 'name'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, RoleModel, 'description'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, RoleModel, 'isAdministrative'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, RoleModel, 'isEssential')
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<RoleModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelErrorAction<RoleModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });

    this.store
      .select(fromStore.getRoleSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: RoleModel) => {
        if (v) {
          this.role = v;
          if (!this.profileForm) {
            this.initForms();
          }
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      key: [''],
      type: [{ value: null, disabled: true }],
      name: this.formBuilder.control('',
        Validators.required,
        null,
        this.destroyed$,
        (control) => {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, RoleModel, 'name', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      description: this.formBuilder.control('',
        Validators.required,
        null,
        this.destroyed$,
        (control) => {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, RoleModel, 'description', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      isAdministrative: this.formBuilder.control('',
        Validators.required,
        null,
        this.destroyed$,
        (control) => {
          this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, RoleModel, 'isAdministrative', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
        isEssential: this.formBuilder.control('',
          Validators.required,
          null,
          this.destroyed$,
          (control) => {
            this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, RoleModel, 'isEssential', { key: this.key, model: control.value, control: control }));
          },
          this.changeDetectorRef)
    });

    this.profileForm.patchValue(this.role);
    this.profileForm.markAsPristine();
  }

  lookupDisplay(lookup?: LookupModel): string | undefined {
    return lookup ? lookup.description : undefined;
  }

  showManageRoles(): void {
    var context: ManageRolesContext = {
      roleType: undefined,
      forRole: this.role,
      assignedRoles$: this.assignedRoles$,
      addRole: (role) => {
        this.store.dispatch(new securityActions.AddRoleToRoleAction({ roleKey: this.role.key, role: role }));
      },
      removeRole: (role) => {
        this.store.dispatch(new securityActions.RemoveRoleFromRoleAction({ roleKey: this.role.key, role: role }));
      },
      loadIncludedRoles: (role) => {}
  };
    this.dialog.open(ManageRolesDialogComponent,
      {
        panelClass: 'customized-dialog',
        height: '80vh',
        width: '80vw',
        data: context
      });
  }

  showManagePermissions(): void {
    var context = {
      assignedPermissions$: this.assignedPermissions$,
      addPermission: (permission) => {
        this.store.dispatch(new securityActions.AddPermissionToRoleAction({ roleKey: this.role.key, permission: permission }));
      },
      removePermission: (permission) => {
        this.store.dispatch(new securityActions.RemovePermissionToRoleAction({ roleKey: this.role.key, permission: permission }));
      }
    };
    this.dialog.open(ManagePermissionsDialogComponent,
      {
        panelClass: 'customized-dialog',
        width: '80vw',
        data: context
      });
  }

  back() {
    this.router.navigateByUrl('/administration');
  }

  delete() {
    this.dialog.open(DeleteRoleConfirmationDialogComponent,
      {
        panelClass: 'customized-dialog',
        width: '648px',
        data:
        {
          name: this.role.name,
          key: this.key
        }
      });
  }
}




// WEBPACK FOOTER //
// ./src/app/security/components/role-workspace.component.ts